var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { BaseInterval } from '@shared/utils/History/BaseInterval';
import { ReloadHistoryParams } from '@shared/utils/History/ReloadHistoryParams';
import { SessionInfo } from '@shared/utils/History/SessionInfo';
import { Periods, TFInfo } from '@shared/utils/History/TFInfo';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { DataCache } from '../../../../DataCache';
import { CashItem } from '../../../History/CashItem';
import { OptionTraderUtils } from '../OptionTraderUtils';
import { HistoricalVolatilityData } from './HistoricalVolatilityData';
import { HistoricalVolatilityPriceType } from './HistoricalVolatilityPriceType';
import { StrikesAggregationStyle } from './StrikesAggregationStyle';
import { StrikesColoringMethod } from './StrikesColoringMethod';
export class OptionChain {
    constructor() {
        this.HV_HISTORY_REQUEST_MS = 60 * 24 * 60 * 60 * 1000;
        this.STD_PERIOD = 21;
        this.VOLATILITY_PERIOD = 365;
        this.strikesAggregationStyle = StrikesAggregationStyle.FromATMStrike;
        this.coloringMethod = StrikesColoringMethod.Classic;
        this.historicalVolatilityPriceType = HistoricalVolatilityPriceType.Last;
        this.customUnderlierPrice = 0;
        this.currentHVPercent = 0;
        this.inTheMoneyColor = '';
        this.outTheMoneyColor = '';
        this.atmStrikeColor = '';
        this.standardDeviationColor = '';
        this.secondStandardDeviationColor = '';
        this.askColor = '';
        this.bidColor = '';
    }
    getColorInOutMoney(account, underlier, option) {
        if (isNullOrUndefined(account) || isNullOrUndefined(underlier) || isNullOrUndefined(option)) {
            return undefined;
        }
        return this.isInTheMoneyOption(account, underlier, option.PutCall === OptionPutCall.OPTION_PUT_VANILLA, option.StrikePrice)
            ? this.inTheMoneyColor
            : this.outTheMoneyColor;
    }
    isInTheMoneyOption(account, underlier, isPut, strikePrice) {
        const last = underlier.Level1.GetLastPrice(account);
        const price = isValidNumber(last)
            ? last
            : (underlier.Level1.GetBid(account) + underlier.Level1.GetAsk(account)) / 2;
        if (isPut) {
            return strikePrice > price;
        }
        else {
            return strikePrice < price;
        }
    }
    isAllowHVColoring(underlier) {
        return (!isNullOrUndefined(underlier) &&
            !isNullOrUndefined(underlier.SaveQuotesHistory) &&
            underlier.SaveQuotesHistory);
    }
    CalculateHistoricalVolatilityAsync(account, underlier) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isNullOrUndefined(account) || isNullOrUndefined(underlier)) {
                return 0;
            }
            const to = DateTimeUtils.DateTimeUtcNow();
            const from = new Date(to.getTime() - this.HV_HISTORY_REQUEST_MS);
            const tfInfo = new TFInfo();
            tfInfo.Periods = Periods.DAY;
            tfInfo.HistoryType = underlier.HistoryType;
            tfInfo.SessionInfo = new SessionInfo(false);
            tfInfo.Plan = DataCache.GetSpreadPlan(account);
            const historyParams = new ReloadHistoryParams();
            historyParams.updateWithProps({
                account,
                instrument: underlier,
                TimeFrameInfo: tfInfo,
                FromTime: from,
                ToTime: to,
                UseDefaultInstrumentHistoryType: false
            });
            const cashItem = yield CashItem.CreateWithHistory(underlier, historyParams);
            if (isNullOrUndefined(cashItem) || cashItem.FNonEmptyCashArrayCount <= this.STD_PERIOD) {
                return 0;
            }
            const lnData = [];
            const lastIdx = cashItem.FNonEmptyCashArrayCount - this.STD_PERIOD;
            for (let i = cashItem.FNonEmptyCashArrayCount - 1; i >= lastIdx; i--) {
                const curPrice = cashItem.FNonEmptyCashArray[i].Data[BaseInterval.CLOSE_INDEX];
                const prevPrice = cashItem.FNonEmptyCashArray[i - 1].Data[BaseInterval.CLOSE_INDEX];
                lnData.push(Math.log(curPrice / prevPrice));
            }
            // calc the standard deviation by the data
            const sumOfDerivationAverage = lnData.reduce((acc, value) => acc + value * value, 0) / this.STD_PERIOD;
            const average = lnData.reduce((acc, value) => acc + value, 0) / lnData.length;
            const standardDeviation = Math.sqrt(sumOfDerivationAverage - average * average);
            return standardDeviation * Math.sqrt(this.VOLATILITY_PERIOD) * 100;
        });
    }
    getHistoricalVolatilityData(account, underlier) {
        if (isNullOrUndefined(account) || isNullOrUndefined(underlier)) {
            return undefined;
        }
        const daysToExpire = OptionTraderUtils.getDaysToExpirationForInstrument(underlier);
        const hvPrice = this.calculatePriceForHistoricalVolatility(account, underlier, this.historicalVolatilityPriceType);
        const lvl1Deviation1 = this.calculateDeviation(true, daysToExpire, hvPrice, 1);
        const lvl1Deviation2 = this.calculateDeviation(false, daysToExpire, hvPrice, 1);
        const lvl2Deviation1 = this.calculateDeviation(true, daysToExpire, hvPrice, 2);
        const lvl2Deviation2 = this.calculateDeviation(false, daysToExpire, hvPrice, 2);
        const data = new HistoricalVolatilityData();
        data.lastPrice = underlier.Level1.GetLastPrice(account);
        data.lvl1Left = Math.min(lvl1Deviation1, lvl1Deviation2);
        data.lvl1Right = Math.max(lvl1Deviation1, lvl1Deviation2);
        data.lvl2Left = Math.min(lvl2Deviation1, lvl2Deviation2);
        data.lvl2Right = Math.max(lvl2Deviation1, lvl2Deviation2);
        return data;
    }
    calculatePriceForHistoricalVolatility(account, underlier, type) {
        switch (type) {
            case HistoricalVolatilityPriceType.Last:
                return underlier.Level1.GetLastPrice(account);
            case HistoricalVolatilityPriceType.BidAsk:
                return (underlier.Level1.GetAsk(account) + underlier.Level1.GetBid(account)) / 2;
            case HistoricalVolatilityPriceType.OHL: {
                const open = underlier.InstrumentDayInfo.getOpen(account);
                const high = underlier.InstrumentDayInfo.getHigh(account);
                const low = underlier.InstrumentDayInfo.getLow(account);
                return (open + high + low) / 3;
            }
            case HistoricalVolatilityPriceType.OHLC: {
                const open = underlier.InstrumentDayInfo.getOpen(account);
                const high = underlier.InstrumentDayInfo.getHigh(account);
                const low = underlier.InstrumentDayInfo.getLow(account);
                const last = underlier.Level1.GetLastPrice(account);
                return (open + high + low + last) / 4;
            }
            default:
                return 0;
        }
    }
    calculateDeviation(isTop, daysToExpire, hvPrice, multiplier) {
        if (daysToExpire <= 0) {
            return 0;
        }
        const hv = this.currentHVPercent / 100;
        const res = multiplier * ((hv / Math.sqrt(this.VOLATILITY_PERIOD)) * Math.sqrt(daysToExpire));
        return hvPrice * (1 + (isTop ? res : -res));
    }
}
